import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { bindActionCreators } from 'redux';
import { changePlanOrder } from '../../actions/orderActions';
import { ReactComponent as Success } from '../../assets/checked.svg';
import {
  orderDetailsSelector,
  plansSelector,
} from '../../selectors/bundleSelectors';
import { ORDER_DETAIL_ACTION } from '../../utils/const';
import { SOURCE_CHANGE_OPTIONS } from '../../utils/const';
import {
  getFirstTrialPlan as _getFirstTrialPlan,
  isFreePlan,
  showPicker,
  toCapitalize,
} from '../../utils/helper';
import styles from './ChangePlan.module.scss';
import { isOrderStatusActive } from '../utils';
import { SourceSelector } from '../SourceSelector';
import { ORDER_TYPES } from '../../helpers/constants';
import { SOURCE_HOOKS } from '../../utils/const';
import medusa from '../../utils/medusa';

const tomorrow = moment().add(1, 'day').startOf('day');

const CHANGE_PLAN_RESTRICTED_PLANS = ['free_site'];

const ChangePlanOrder = (props) => {
  const {
    domainName,
    domainId,
    accountId,
    action,
    orderType,
    isFreePlan: propIsFreePlan,
    closeModal,
    intl,
    totalAccountsInUse,
    changePlanOrder,
    token,
    currentRegion,
    orderChangePlanError,
    isOrderChangePlanInProgress,
    isOrderChangePlanSuccess,
    noOfAccountsExceedingQuota,
    fetchAccountExceedingQuotaError,
  } = props;

  const minDate = tomorrow.clone().format('YYYY-MM-DD');

  // Local state using hooks
  const [reason, setReason] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [newExpiryDate, setNewExpiryDate] = useState(null);
  const [source, setSource] = useState('');
  const [otherSource, setOtherSource] = useState('');

  const allAvailablePlans = useSelector(plansSelector);
  const allOrders = useSelector(orderDetailsSelector);
  const availablePlans = allAvailablePlans.filter(
    (p) => p.orderType === orderType && isOrderStatusActive(p.status)
  );
  const [selectedPlanId, setSelectedPlanId] = useState(
    availablePlans[0]?.id || null
  );

  const currentOrder = allOrders.filter((order) => order.type === orderType)[0];
  const currentPlan = currentOrder?.plan;
  const [currentOrderId, setCurrentOrderId] = useState(null);

  const isSourceRequired = [
    ORDER_DETAIL_ACTION.CHANGE_PLAN,
    ORDER_DETAIL_ACTION.EXTEND_PLAN,
  ].includes(action);

  const selectedSource =
    source === SOURCE_CHANGE_OPTIONS.OTHER_REQUEST ? otherSource : source;

  useEffect(() => {
    const order = allOrders.filter((order) => order.type === orderType)[0];
    setCurrentOrderId(order.id);
  }, [allOrders, orderType]);

  // Helpers
  const getPlanDetails = (planId) =>
    availablePlans.find((p) => p.id === planId);

  const _isFreePlan = (planId) => {
    const selectedPlan = getPlanDetails(planId);
    return isFreePlan(selectedPlan);
  };

  const getFirstTrialPlan = () => _getFirstTrialPlan(availablePlans);

  // On mount, if free plan, auto-select the trial plan
  useEffect(() => {
    if (propIsFreePlan) {
      const firstTrialPlan = getFirstTrialPlan()?.id;
      if (firstTrialPlan) {
        setSelectedPlanId(firstTrialPlan);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const changeReason = (e) => {
    setReason(e.target.value);
  };

  const changeExpiryDate = (e) => {
    if (e.target.value) setNewExpiryDate(e.target.value);
  };

  const updatePlanId = (e) => {
    setSelectedPlanId(Number(e.target.value));
  };

  const updateSource = (e) => {
    setSource(e.target.value);
  };

  const updateSourceOtherText = (e) => {
    setOtherSource(e.target.value);
  };

  const resetForm = () => {
    setReason('');
    setFormErrors({});
    setNewExpiryDate(null);
    setSelectedPlanId(availablePlans[0]?.id || null);
  };

  const handleCloseModal = () => {
    resetForm();
    closeModal(null, null);
  };

  const validateForm = () => {
    const errors = {};
    if (!selectedPlanId) {
      errors.planType = intl.formatMessage({ id: 'string.empty' });
    } else if (!reason || !reason.trim()) {
      errors.reason = intl.formatMessage({ id: 'string.empty' });
    }
    if (isSourceRequired && !source) {
      errors.source = intl.formatMessage({ id: 'string.empty' });
    }
    if (
      isSourceRequired &&
      source === SOURCE_CHANGE_OPTIONS.OTHER_REQUEST &&
      !otherSource
    ) {
      errors.otherSource = intl.formatMessage({ id: 'string.empty' });
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const createMedusaCommonPayload = () => {
    return {
      orderType: [orderType],
      orderStatus: currentOrder.status,
      renewalDt: newExpiryDate || currentOrder.expiryDate,
      oldRenewalDt: currentOrder.expiryDate,
      reason,
      sourceHook: SOURCE_HOOKS.CHANGE_PLAN_MODAL,
      source: selectedSource,
    };
  };

  const createPlanChangedMedusaPayload = () => {
    let payload = createMedusaCommonPayload();
    if (orderType === ORDER_TYPES.MAIL) {
      payload = {
        ...payload,
        mailPlanType: getPlanDetails(selectedPlanId)?.planType,
        oldMailPlanType: currentPlan?.planType,
      };
    } else if (orderType === ORDER_TYPES.SITE) {
      payload = {
        ...payload,
        sitePlanType: getPlanDetails(selectedPlanId)?.planType,
        oldSitePlanType: currentPlan?.planType,
      };
    } else if (orderType === ORDER_TYPES.DOMAIN) {
      payload = {
        ...payload,
        domainPlanType: getPlanDetails(selectedPlanId)?.planType,
        oldDomainPlanType: currentPlan?.planType,
      };
    }
    return payload;
  };

  const handleButtonClick = async () => {
    if (
      action === ORDER_DETAIL_ACTION.CHANGE_PLAN &&
      CHANGE_PLAN_RESTRICTED_PLANS.includes(currentPlan.planType)
    ) {
      toast.info('Changing plan is not allowed for free site');
      return false;
    }
    const renewalDateMedusaPayload = createMedusaCommonPayload();
    const planChangedMedusaPayload = createPlanChangedMedusaPayload();

    if (!validateForm()) return false;

    const requestPayload = {
      action,
      accountId,
      newExpiry: newExpiryDate,
      orderId: currentOrderId,
      noOfAccounts: totalAccountsInUse,
      planId:
        action === ORDER_DETAIL_ACTION.EXTEND_PLAN
          ? currentPlan.id
          : selectedPlanId,
      reason,
    };

    changePlanOrder(requestPayload, token, currentRegion);
    if (!isSelectPlanAFreePlan || isExtendPlan) {
      await medusa.markRenewalDateChanged(renewalDateMedusaPayload);
    }
    if (!propIsFreePlan && !isExtendPlan) {
      await medusa.markPlanChanged(planChangedMedusaPayload);
    }
  };

  // Render logic
  let buttonText, modalHeading, modalMessage;
  let cancelButtonText = intl.formatMessage({ id: 'cancel' });
  const isSelectPlanAFreePlan = _isFreePlan(selectedPlanId);
  const isExtendPlan = action === ORDER_DETAIL_ACTION.EXTEND_PLAN;

  switch (action) {
    case ORDER_DETAIL_ACTION.MOVE_TO_TRIAL_PLAN: {
      const currentPlan = getPlanDetails(selectedPlanId);
      const intlProps = {
        domainName,
        planName: currentPlan?.name,
      };
      buttonText = intl.formatMessage(
        { id: 'move-to-trial' },
        { planName: currentPlan?.name }
      );
      modalHeading = intl.formatMessage(
        { id: 'move-to-trial-modal-heading' },
        intlProps
      );
      modalMessage = intl.formatMessage(
        { id: 'move-to-trial-modal-message' },
        intlProps
      );
      break;
    }
    case ORDER_DETAIL_ACTION.EXTEND_PLAN: {
      const planName = toCapitalize(currentPlan.name);
      buttonText = intl.formatMessage(
        { id: 'extend-current-plan-modal-button' },
        { plan: planName }
      );
      modalHeading = intl.formatMessage(
        { id: 'extend-current-plan-modal-heading' },
        { domainName, plan: planName }
      );
      modalMessage = intl.formatMessage(
        { id: 'extend-current-plan-modal-message' },
        { plan: planName, domainName }
      );
      break;
    }
    case ORDER_DETAIL_ACTION.CHANGE_PLAN: {
      let noOfAccounts = noOfAccountsExceedingQuota;
      if (fetchAccountExceedingQuotaError) {
        modalHeading = intl.formatMessage({
          id: 'unable-to-fetch-mailbox-quota',
        });
        modalMessage = intl.formatMessage({
          id: 'free-storage-exceeded-error',
        });
        buttonText = intl.formatMessage({ id: 'continue-downgrade' });
        cancelButtonText = intl.formatMessage({
          id: 'cancel-and-check-manually',
        });
      } else if (noOfAccounts > 0) {
        const mailboxString =
          noOfAccounts === 1
            ? intl.formatMessage({ id: 'mailbox' })
            : intl.formatMessage({ id: 'mailboxes' });
        modalHeading = intl.formatMessage(
          { id: 'account-exceeding-quota-modal-heading' },
          { numberOfAccounts: noOfAccounts, mailbox: mailboxString }
        );
        modalMessage = intl.formatMessage(
          { id: 'account-exceeding-quota-modal-message' },
          { numberOfAccounts: noOfAccounts, mailbox: mailboxString }
        );
        buttonText = intl.formatMessage({ id: 'continue-downgrade' });
        cancelButtonText = intl.formatMessage({
          id: 'cancel-and-inform-customer',
        });
      } else if (noOfAccounts === 0) {
        const selectedPlan = getPlanDetails(selectedPlanId);
        const intlProps = {
          domainName,
          currentPlan: currentPlan.name,
          newPlanName: selectedPlan.name,
        };
        buttonText = intl.formatMessage({ id: 'change-plan' });
        modalHeading = intl.formatMessage(
          { id: 'change-plan-modal-heading' },
          intlProps
        );
        modalMessage = intl.formatMessage(
          { id: 'change-plan-modal-message' },
          intlProps
        );
      }
      break;
    }
    default:
      break;
  }

  if (isOrderChangePlanSuccess) {
    return (
      <div className={styles.changePlanSuccess}>
        <Success />
        <span className={styles.successHeader}>
          <FormattedMessage id="congratulations" />
        </span>
        <span className="configure-trial-success-message">
          {action === ORDER_DETAIL_ACTION.EXTEND_PLAN ? (
            <FormattedMessage id="plan-updated-successful" />
          ) : (
            <FormattedMessage id="plan-change-successful" />
          )}
        </span>
        <div className="configure-trial-success-button-div">
          <button
            className="button button-primary configure-trial-success-button"
            onClick={() => closeModal(domainId, accountId)}
          >
            <FormattedMessage id="close" />
          </button>
        </div>
      </div>
    );
  } else {
    return (
      <div className="delete-order-content">
        <span className="delete-orders-header">{modalHeading}</span>
        {orderChangePlanError && !isOrderChangePlanInProgress && (
          <div className="delete-order-error">{orderChangePlanError}</div>
        )}
        <div className="delete-orders-message">
          <form>
            {modalMessage}
            {!propIsFreePlan && !isExtendPlan && (
              <fieldset className="t-form-field mt2">
                <FormattedMessage
                  className="col"
                  tagName="label"
                  id="select-plan-type"
                />
                <select
                  className="block"
                  value={selectedPlanId}
                  onChange={updatePlanId}
                >
                  {availablePlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (ID: {p.id} Type: {p.planType})
                    </option>
                  ))}
                </select>
                {formErrors?.planType && <span>{formErrors.planType}</span>}
              </fieldset>
            )}
            {(!isSelectPlanAFreePlan || isExtendPlan) && (
              <fieldset className="t-form-field mt2">
                <FormattedMessage
                  className="col"
                  tagName="label"
                  id="new-expiry"
                />
                <input
                  type="date"
                  min={minDate}
                  onFocus={showPicker}
                  onChange={changeExpiryDate}
                  value={newExpiryDate || ''}
                />
              </fieldset>
            )}
            <span className="t-separator"></span>
            <fieldset className="t-form-field ">
              <FormattedMessage tagName="label" id="change-plan-reason-label" />
              <input
                placeholder={intl.formatMessage({ id: 'change-plan-reason' })}
                onChange={changeReason}
                value={reason}
              />
              {formErrors?.reason && <span>{formErrors.reason}</span>}
            </fieldset>
            {isSourceRequired && (
              <SourceSelector
                onSourceChange={updateSource}
                onOtherSourceChange={updateSourceOtherText}
                otherSourceError={formErrors?.otherSource}
                sourceError={formErrors?.source}
                otherSource={otherSource}
                source={source}
              />
            )}
          </form>
        </div>
        <div className="delete-buttons">
          <button
            className="button button-primary delete-order-button"
            onClick={handleButtonClick}
            disabled={isOrderChangePlanInProgress}
          >
            {buttonText}
          </button>
          <button
            className="button button-cancel delete-order-button"
            onClick={handleCloseModal}
            disabled={isOrderChangePlanInProgress}
          >
            {cancelButtonText}
          </button>
        </div>
      </div>
    );
  }
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
  isOrderChangePlanInProgress: state.orders.isOrderChangePlanInProgress,
  isOrderChangePlanSuccess: state.orders.isOrderChangePlanSuccess,
  orderChangePlanError: state.orders.orderChangePlanError,
  noOfAccountsExceedingQuota: state.orders.noOfAccountsExceedingQuota,
  fetchAccountExceedingQuotaError: state.orders.fetchAccountExceedingQuotaError,
});

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators({ changePlanOrder }, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(ChangePlanOrder));
